import {
  Button,
  DatePicker,
  FilePicker,
  Label,
  PhoneInput,
  RadioList,
  Select,
  SelectOption,
  TextArea,
  TextInput,
  useValidatedState,
} from "acma-spa";
import React, { useEffect, useState } from "react";
import { DropEvent, FileRejection } from "react-dropzone";
import * as yup from "yup";
import {
  getApiClient,
  IPhoneProvider,
  ISocialMediaPlatform,
  PhoneProvider,
} from "../api";
import {
  ISPAMComplaintMessage,
  SPAMComplaintMessage,
  SPAMComplaintMessageType,
} from "../models/Models";
import { PhoneProviderPicker } from "../pickers/PhoneProviderPicker";
import { isValidPhoneNumber } from "react-phone-number-input";
import { E164Number } from "libphonenumber-js";

export interface MessageProps {
  r: ISPAMComplaintMessage | null;
  errors: string;
  onChange: (t: ISPAMComplaintMessage | null) => void;
  baseUrl: string;
  anonymous: boolean;
}

const messageTypeOptions = () => {
  let options: SelectOption<SPAMComplaintMessageType>[] = [];
  options.push({ label: "", value: undefined });
  for (const key in SPAMComplaintMessageType) {
    if (typeof SPAMComplaintMessageType[key] === "string") {
      options.push({
        label: SPAMComplaintMessageType[key],
        value: SPAMComplaintMessageType[key] as SPAMComplaintMessageType,
      });
    }
  }
  return options;
};

const validatePhone = async (phone: string | undefined) => {
  if (typeof phone === "undefined") return true;
  return isValidPhoneNumber(phone);
};

const submissionSchema: yup.ObjectSchema<ISPAMComplaintMessage> = yup.object({
  file: yup.mixed(),
  fileId: yup.string(),
  anonymous: yup.bool(),
  uploadMessage: yup.bool().required().label("This"),
  pasteMessage: yup.string().when("uploadMessage", {
    is: false,
    then: (schema) => schema.required().label("This").max(100000),
    otherwise: (schema) => schema,
  }),
  allowMarketing: yup.bool().required().label("This"),
  allowMarketingInfo: yup.string().when("allowMarketing", {
    is: true,
    then: (schema) => schema.required().label("This").max(2000),
    otherwise: (schema) => schema,
  }),
  attemptUnsubscribe: yup.bool().required().label("This"),
  attemptUnsubscribeInfo: yup.string().when("attemptUnsubscribe", {
    is: true,
    then: (schema) => schema.required().label("This").max(2000),
    otherwise: (schema) => schema,
  }),
  isCustomer: yup.bool().required().label("This"),
  isCustomerInfo: yup.string().when("isCustomer", {
    is: true,
    then: (schema) => schema.required().label("This").max(2000),
    otherwise: (schema) => schema,
  }),
  scam: yup.bool().required().label("This"),
  messageDate: yup
    .date()
    .required()
    .label("This")
    .max(new Date(), "This must be earlier or on today")
    .nullable()
    .typeError("Invalid Date"),
  messageType: yup
    .mixed<SPAMComplaintMessageType>()
    .oneOf(Object.values(SPAMComplaintMessageType))
    .required()
    .label("This"),
  businessName: yup.string().label("This").max(100),
  fromEmail: yup.string().when("messageType", {
    is: SPAMComplaintMessageType.Email,
    then: (schema) => schema.email().required().label("This").max(100),
    otherwise: (schema) => schema,
  }),
  toEmail: yup.string().when(["messageType", "anonymous"], {
    is: (messageType: SPAMComplaintMessageType, anonymous) =>
      anonymous === false && messageType === SPAMComplaintMessageType.Email,
    then: (schema) => schema.email().required().label("This").max(100),
    otherwise: (schema) => schema,
  }),
  subject: yup.string().label("This").max(100),
  fromPhone: yup.string().when("messageType", {
    is: SPAMComplaintMessageType.SMS_MMS,
    then: (schema) => schema.required().label("This").max(100),
    otherwise: (schema) => schema,
  }),
  toPhone: yup.string().when(["messageType", "anonymous"], {
    is: (messageType, anonymous) =>
      anonymous === false && messageType === SPAMComplaintMessageType.SMS_MMS,
    then: (schema) =>
      schema
        .required()
        .label("This")
        .test("", "Please enter a valid phone number", validatePhone)
        .max(100),
    otherwise: (schema) => schema,
  }),
  phoneProvider: yup.mixed(),
  otherProvider: yup.string().label("This").max(100),
  platform: yup.mixed().when(["messageType"], {
    is: (messageType) =>
      messageType === SPAMComplaintMessageType.Instant_message,
    then: (schema) => schema.required().label("This"),
    otherwise: (schema) => schema,
  }),
  accountName: yup.string().when(["messageType", "anonymous"], {
    is: (messageType, anonymous) =>
      anonymous === false &&
      messageType === SPAMComplaintMessageType.Instant_message,
    then: (schema) => schema.required().label("This").max(100),
    otherwise: (schema) => schema,
  }),
  fileName: yup.string().when("uploadMessage", {
    is: true,
    then: (schema) => schema.required().label("This"),
    otherwise: (schema) => schema,
  }),
  comments: yup.string().label("This").max(2000),
});

export const Message: React.FC<MessageProps> = (props): any => {
  const [platforms, setPlatforms] = useState<ISocialMediaPlatform[]>([]);
  const [platformOptions, setPlatformOptions] = useState<
    SelectOption<string | null>[]
  >([]);

  const [providers, setProviders] = useState<PhoneProvider[]>([]);
  const [providerOptions, setProviderOptions] = useState<
    SelectOption<string | null>[]
  >([]);

  useEffect(() => {
    async function fetchAsync() {
      const [client, abort] = getApiClient(props.baseUrl);

      let platforms = await client.platforms();
      setPlatforms(platforms);
      let platformOptions: SelectOption<string | null>[] = [
        { label: "Select...", value: null },
      ];
      platforms.forEach((p: ISocialMediaPlatform) => {
        platformOptions.push({ label: p.name, value: p.id });
      });
      setPlatformOptions(platformOptions);

      let providers = await client.providers();
      setProviders(providers);
      let providerOptions: SelectOption<string | null>[] = [
        { label: "Select...", value: null },
      ];
      providers.forEach((p: PhoneProvider) => {
        providerOptions.push({ label: p.name, value: p.id });
      });
      setProviderOptions(providerOptions);
    }

    fetchAsync();
  }, []);

  let blank = SPAMComplaintMessage.fromJS({ anonymous: props.anonymous });
  const [message, setMessage, errors, validate] =
    useValidatedState<ISPAMComplaintMessage>(
      props.r || blank,
      submissionSchema
    );

  const handleAdd = async () => {
    await validate("#message-details-container").then(async (result) => {
      if (result) {
        props.onChange(message);
        setMessage(blank);
        window.scrollTo(0, 0);
      }
    });
  };

  useEffect(() => {
    if (!message.uploadMessage) {
      setMessage({ file: undefined, fileName: undefined, fileId: undefined });
    }
  }, [message.uploadMessage]);

  return (
    <>
      <div id="message-details-container">
        <div className="row">
          <div className="col col-12 mb-4">
            <Label
              id="businessName"
              label="Name of the business that sent you the message"
              error={errors.businessName}
            >
              <TextInput
                id="businessName"
                value={message.businessName}
                onChange={(v) => setMessage({ businessName: v })}
                size="block"
              />
            </Label>
          </div>
        </div>
        <div className="row">
          <div className="col col-12 mb-4">
            <Label
              id="messageDate"
              label="Date you received the message"
              error={errors.messageDate}
              required
            >
              <DatePicker
                value={message.messageDate}
                className="mt-2"
                onChange={(v) => setMessage({ messageDate: v })}
              />
            </Label>
          </div>
        </div>
        <div className="row">
          <div className="col col-12 mb-4">
            <Label
              id="messageType"
              label="Type of message"
              error={errors.messageType}
              required
            >
              <Select<SPAMComplaintMessageType>
                id="messageType"
                value={message.messageType}
                onChange={(v) => setMessage({ messageType: v })}
                options={messageTypeOptions()}
                size="block"
              />
            </Label>
          </div>
        </div>
        {message.messageType === SPAMComplaintMessageType.Email && (
          <>
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="fromEmail"
                  label="Email address that sent you the message"
                  error={errors.fromEmail}
                  required
                >
                  <TextInput
                    id="fromEmail"
                    value={message.fromEmail}
                    onChange={(v) => setMessage({ fromEmail: v })}
                    size="block"
                  />
                </Label>
              </div>
            </div>
            {!props.anonymous && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="toEmail"
                    label="Email address where you received the message"
                    error={errors.toEmail}
                    required
                  >
                    <TextInput
                      id="toEmail"
                      value={message.toEmail}
                      onChange={(v) => setMessage({ toEmail: v })}
                      size="block"
                    />
                  </Label>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="subject"
                  label="Subject line of the email"
                  error={errors.subject}
                >
                  <TextInput
                    id="subject"
                    value={message.subject}
                    onChange={(v) => setMessage({ subject: v })}
                    size="block"
                  />
                </Label>
              </div>
            </div>
          </>
        )}
        {message.messageType === SPAMComplaintMessageType.SMS_MMS && (
          <>
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="fromPhone"
                  label="Sender phone number or name displayed"
                  error={errors.fromPhone}
                  required
                >
                  <TextInput
                    id="fromPhone"
                    value={message.fromPhone}
                    onChange={(v) => setMessage({ fromPhone: v })}
                    size="block"
                  />
                </Label>
              </div>
            </div>
            {!props.anonymous && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="toPhone"
                    label="Phone number where you received the message"
                    error={errors.toPhone}
                    required
                  >
                    <PhoneInput
                      id="toPhone"
                      value={message.toPhone as E164Number}
                      onChange={(v) => setMessage({ toPhone: v })}
                      size="block"
                    />
                  </Label>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="phoneProvider"
                  label="Your phone provider"
                  error={errors.phoneProvider}
                >
                  <PhoneProviderPicker
                    id="phoneProvider"
                    baseUrl={props.baseUrl}
                    value={message.phoneProvider}
                    onChange={(provider: IPhoneProvider) => {
                      if (
                        provider.id !== "00000000-0000-0000-0000-000000000000"
                      ) {
                        setMessage({
                          phoneProvider: new PhoneProvider(provider),
                        });
                      } else {
                        setMessage({
                          otherProvider: provider?.name,
                        });
                      }
                    }}
                    size="block"
                  />
                </Label>
              </div>
            </div>
            {message.phoneProvider?.name === "Other" && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="otherProvider"
                    label=""
                    error={errors.otherProvider}
                  >
                    <TextInput
                      id="otherProvider"
                      placeholder="Other phone provider"
                      value={message.otherProvider}
                      onChange={(v) => setMessage({ otherProvider: v })}
                      size="block"
                    />
                  </Label>
                </div>
              </div>
            )}
          </>
        )}
        {message.messageType === SPAMComplaintMessageType.Instant_message && (
          <>
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="platform"
                  label="What platform did you receive the message on?"
                  error={errors.platform}
                  required
                >
                  <Select<string | null>
                    id="platform"
                    value={message.platform?.id}
                    onChange={(v) => {
                      let platform = platforms.filter((i) => i.id == v)[0];
                      setMessage({ platform: platform });
                    }}
                    options={platformOptions}
                    size="block"
                  />
                </Label>
              </div>
            </div>
            {!props.anonymous && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="accountName"
                    label="Your account name or number"
                    error={errors.accountName}
                    required
                  >
                    <TextInput
                      id="accountName"
                      value={message.accountName}
                      onChange={(v) => setMessage({ accountName: v })}
                      size="block"
                    />
                  </Label>
                </div>
              </div>
            )}
          </>
        )}
        {message.messageType === SPAMComplaintMessageType.Phone_call && (
          <>
            <div className="row">
              <div className="col col-12 mb-4">
                <p>
                  The Spam Act does not apply to phone calls. If you have
                  received an unwanted telemarketing or scam call, you should
                  report this through the Do Not Call Register at{" "}
                  <a href="https://www.donotcall.gov.au" target="_blank">
                    www.donotcall.gov.au
                  </a>
                </p>
                <p>
                  Please note: if you continue with this form to report a phone
                  call, we will be unable to take any action in response to your
                  complaint
                </p>
              </div>
            </div>
          </>
        )}
        {message.messageType !== SPAMComplaintMessageType.Phone_call && (
          <>
            {" "}
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="uploadMessage"
                  label="Are you able to upload a copy of the message?"
                  error={errors.uploadMessage}
                  required
                >
                  <RadioList
                    id="uploadMessage"
                    value={message.uploadMessage}
                    options={[
                      {
                        label: "Yes",
                        value: true,
                      },
                      {
                        label: "No",
                        value: false,
                      },
                    ]}
                    onChange={(v) => {
                      setMessage({ uploadMessage: v });
                    }}
                  />
                </Label>
              </div>
            </div>
            {message.uploadMessage === false && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="pasteMessage"
                    label="If you can’t upload the message, copy and paste the message here if possible."
                    hint="Please note, if you do not provide a copy of the message, the action we can take will be limited"
                    error={errors.pasteMessage}
                    required
                  >
                    <TextArea
                      id="pasteMessage"
                      value={message.pasteMessage}
                      onChange={(v) => setMessage({ pasteMessage: v })}
                    />
                  </Label>
                </div>
              </div>
            )}
            {message.uploadMessage && (
              <div className="row">
                <div className="col col-12">
                  <Label
                    id="file"
                    label="Please provide a copy of the message."
                    error={errors.fileName}
                    required
                  >
                    <FilePicker
                      id="file"
                      accept={{
                        "image/png": [],
                        "image/jpeg": [],
                        "image/gif": [],
                        "image/bmp": [],
                        "image/tiff": [],
                        "text/plain": [],
                        "application/pdf": [],
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                          [],
                        "application/vnd.ms-outlook": [".msg"],
                        "message/rfc822": [".eml"],
                      }}
                      onChange={(
                        f: File[],
                        r: FileRejection[],
                        event: DropEvent
                      ) =>
                        setMessage({
                          file: f[0],
                          fileName: f[0]?.name,
                          fileId: crypto.randomUUID(),
                        })
                      }
                      multiple={false}
                      text="Drag 'n' drop to upload txt, eml, msg, .jpeg, .png, .bmp, .pdf, .tiff, .gif, .docx file."
                    />
                  </Label>
                </div>
                <div className="col col-12">
                  <table className="mt-2">
                    <thead>
                      <tr>
                        <th colSpan={2}>File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!message.fileName && (
                        <tr>
                          <td colSpan={2}>
                            <span>No files have been uploaded</span>
                          </td>
                        </tr>
                      )}
                      {message.fileName && (
                        <tr>
                          <td>{message.fileName}</td>
                          <td className="float-right">
                            <a
                              href="#"
                              onClick={() => {
                                setMessage({
                                  file: undefined,
                                  fileName: undefined,
                                  fileId: undefined,
                                });
                              }}
                            >
                              <span>Remove</span>
                            </a>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="allowMarketing"
                  label="Have you ever given your permission to receive marketing messages from the sender, for example, by signing up to a mailing list (if uncertain, select ‘no’)?"
                  error={errors.allowMarketing}
                  required
                >
                  <RadioList
                    id="allowMarketing"
                    value={message.allowMarketing}
                    options={[
                      {
                        label: "Yes",
                        value: true,
                      },
                      {
                        label: "No",
                        value: false,
                      },
                    ]}
                    onChange={(v) => setMessage({ allowMarketing: v })}
                  />
                </Label>
              </div>
            </div>
            {message.allowMarketing && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="allowMarketingInfo"
                    label="Please describe"
                    error={errors.allowMarketingInfo}
                    required
                  >
                    <TextArea
                      id="allowMarketingInfo"
                      value={message.allowMarketingInfo}
                      onChange={(v) => setMessage({ allowMarketingInfo: v })}
                    />
                  </Label>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="isCustomer"
                  label="Do you have a relationship with the sender? For example, you may be a past or current customer."
                  error={errors.isCustomer}
                  required
                >
                  <RadioList
                    id="isCustomer"
                    value={message.isCustomer}
                    options={[
                      {
                        label: "Yes",
                        value: true,
                      },
                      {
                        label: "No",
                        value: false,
                      },
                    ]}
                    onChange={(v) => setMessage({ isCustomer: v })}
                  />
                </Label>
              </div>
            </div>
            {message.isCustomer && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="isCustomerInfo"
                    label="Tell us about the relationship"
                    error={errors.isCustomerInfo}
                    required
                  >
                    <TextArea
                      id="isCustomerInfo"
                      value={message.isCustomerInfo}
                      onChange={(v) => setMessage({ isCustomerInfo: v })}
                    />
                  </Label>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="attemptUnsubscribe"
                  label="Did you attempt to unsubscribe, stop or opt out before you received this message?"
                  error={errors.attemptUnsubscribe}
                  required
                >
                  <RadioList
                    id="attemptUnsubscribe"
                    value={message.attemptUnsubscribe}
                    options={[
                      {
                        label: "Yes",
                        value: true,
                      },
                      {
                        label: "No",
                        value: false,
                      },
                    ]}
                    onChange={(v) => setMessage({ attemptUnsubscribe: v })}
                  />
                </Label>
              </div>
            </div>
            {message.attemptUnsubscribe && (
              <div className="row">
                <div className="col col-12 mb-4">
                  <Label
                    id="unsubscribed"
                    label="When and how did you unsubscribe?"
                    hint="Please note, businesses have up to 5 business days to unsubscribe you after you make a request"
                    error={errors.attemptUnsubscribeInfo}
                    required
                  >
                    <TextArea
                      id="unsubscribed"
                      value={message.attemptUnsubscribeInfo}
                      onChange={(v) =>
                        setMessage({ attemptUnsubscribeInfo: v })
                      }
                    />
                  </Label>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="scam"
                  label="Do you believe the message you received was a scam?"
                  error={errors.scam}
                  required
                >
                  <RadioList
                    id="scam"
                    value={message.scam}
                    options={[
                      {
                        label: "Yes",
                        value: true,
                      },
                      {
                        label: "No",
                        value: false,
                      },
                    ]}
                    onChange={(v) => setMessage({ scam: v })}
                  />
                </Label>
              </div>
            </div>
            <div className="row">
              <div className="col col-12 mb-4">
                <Label
                  id="comments"
                  label="Any further comments about your complaint?"
                  error={errors.comments}
                >
                  <TextArea
                    id="comments"
                    value={message.comments}
                    onChange={(v) => setMessage({ comments: v })}
                  />
                </Label>
              </div>
            </div>
            <div className="row">
              <div className="col col-12 mb-4">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <Button
                      label={props.r ? "Save message" : "Save message"}
                      onClick={() => handleAdd()}
                    />
                    <Button
                      label="Cancel"
                      mode="secondary"
                      onClick={() => {
                        props.onChange(props.r);
                        window.scrollTo(0, 0);
                      }}
                      className="ml-2"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
